import React, { useState, useMemo } from "react";

export interface FilterState {
  category: string;
  gender: string[];
  sizes: string[];
  brands: string[];
  collarTypes: string[];
  fits: string[];
  minPrice: string;
  maxPrice: string;
  minDiscount: number | null;
  stores: string[];
  searchKeyword: string;
}

interface Props {
  filters: FilterState;
  onChange: (newFilters: FilterState) => void;
  onReset: () => void;
  availableBrands?: string[];
  totalResultsCount: number;
}

const CATEGORIES = [
  { id: "all", label: "Tüm İndirimli Ürünler", icon: "ri-apps-2-line" },
  { id: "erkek-tisort", label: "Tişört & Polo", icon: "ri-t-shirt-line" },
  { id: "erkek-gomlek", label: "Gömlek", icon: "ri-shirt-line" },
  { id: "erkek-pantolon", label: "Pantolon & Jean", icon: "ri-pantone-line" },
  { id: "erkek-takim", label: "Takım Elbise", icon: "ri-user-star-line" },
  { id: "erkek-mont", label: "Mont & Dış Giyim", icon: "ri-contrast-drop-2-line" },
  { id: "erkek-spor-ayakkabi", label: "Spor Ayakkabı & Sneaker", icon: "ri-footprint-line" },
  { id: "elektronik", label: "Telefon & Akıllı Cihaz", icon: "ri-smartphone-line" },
  { id: "parfum", label: "Kozmetik & Parfüm", icon: "ri-magic-line" },
  { id: "ev-yasam", label: "Ev & Yaşam / Airfryer", icon: "ri-home-4-line" },
];

const GENDERS = ["Erkek", "Kadın", "Unisex", "Çocuk"];
const CLOTHING_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];
const SHOE_SIZES = ["36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46"];
const PANTS_SIZES = ["28", "29", "30", "31", "32", "33", "34", "36", "38", "S", "M", "L", "XL"];
const SUIT_SIZES = ["46", "48", "50", "52", "54", "56", "58"];
const TECH_SPECS = ["64 GB", "128 GB", "256 GB", "512 GB", "1 TB", "40mm", "44mm", "ANC"];
const COSMETIC_VOLUMES = ["30 ml", "50 ml", "75 ml", "100 ml", "150 ml", "200 ml"];
const HOME_CAPACITIES = ["XXL", "XL", "1.9 Litre", "1.4kg", "Lazer Başlık", "60 Dk"];

const COLLAR_TYPES = ["Bisiklet Yaka", "Polo Yaka", "V Yaka", "Hakim Yaka", "Düğmeli Yaka", "Kapüşonlu"];
const CLOTHING_FITS = ["Slim Fit", "Regular Fit", "Oversize", "Relaxed Fit", "Comfort Fit"];
const PANTS_FITS = ["Slim Fit", "Skinny", "Straight / Düz", "Regular Fit", "Oversize / Baggy"];

const STORES = [
  "Trendyol", "Hepsiburada", "Amazon TR", "D'S Damat", 
  "LC Waikiki", "DeFacto", "Koton", "Colin's", "MediaMarkt", "Teknosa", "Vatan"
];

// Kategoriye Göre Özelleştirilmiş Marka Havuzları
const BRAND_POOLS: Record<string, string[]> = {
  shoes: ["Nike", "Adidas", "Puma", "New Balance", "Skechers", "Vans", "Converse", "Lumberjack"],
  clothing: ["Nike", "Adidas", "Tommy Hilfiger", "Mavi", "Levi's", "D'S Damat", "LC Waikiki", "DeFacto", "Koton", "Colin's", "The North Face", "Zara"],
  tech: ["Apple", "Samsung", "Sony", "JBL", "Logitech", "Xiaomi", "Philips", "Dyson", "Roborock", "Asus"],
  cosmetics: ["Dior", "Chanel", "Tom Ford", "La Roche-Posay", "Yves Saint Laurent", "Clinique", "Calvin Klein"],
  home: ["Philips", "Karaca", "Dyson", "Tefal", "Roborock", "Stanley", "Quechua", "DeLonghi"]
};

export default function AkakceFilterSidebar({
  filters,
  onChange,
  onReset,
  totalResultsCount
}: Props) {
  const [brandSearch, setBrandSearch] = useState("");
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    category: true,
    gender: true,
    size: true,
    brand: true,
    collar: true,
    fit: true,
    price: true,
    discount: true,
    store: true
  });

  const cat = (filters.category || "all").toLowerCase();

  // Kategori Tipi Tespiti
  const isShoe = cat.includes("ayakkabi") || cat.includes("sneaker") || cat.includes("bot");
  const isPants = cat.includes("pantolon") || cat.includes("jean") || cat.includes("chino");
  const isSuit = cat.includes("takim") || cat.includes("smokin");
  const isUpperClothing = cat.includes("tisort") || cat.includes("polo") || cat.includes("gomlek") || cat.includes("sweat") || cat.includes("kazak") || cat.includes("triko") || cat.includes("mont") || cat.includes("kaban") || cat === "giyim";
  const isTech = cat.includes("telefon") || cat.includes("saat") || cat.includes("kulaklik") || cat.includes("tablet") || cat.includes("elektronik");
  const isCosmetics = cat.includes("parfum") || cat.includes("kozmetik") || cat.includes("cilt");
  const isHome = cat.includes("airfryer") || cat.includes("kahve") || cat.includes("supurge") || cat.includes("ev-yasam") || cat.includes("evyasam") || cat.includes("outdoor");

  // Dinamik Beden / Ölçü Listesi ve Başlığı
  const { sizeTitle, sizeList } = useMemo(() => {
    if (isShoe) {
      return { sizeTitle: "Ayakkabı Numarası", sizeList: SHOE_SIZES };
    }
    if (isPants) {
      return { sizeTitle: "Bel / Beden Ölçüsü", sizeList: PANTS_SIZES };
    }
    if (isSuit) {
      return { sizeTitle: "Drop / Takım Bedeni", sizeList: SUIT_SIZES };
    }
    if (isTech) {
      return { sizeTitle: "Dahili Hafıza & Kapasite", sizeList: TECH_SPECS };
    }
    if (isCosmetics) {
      return { sizeTitle: "Hacim / Boyut", sizeList: COSMETIC_VOLUMES };
    }
    if (isHome) {
      return { sizeTitle: "Kapasite & Boyut", sizeList: HOME_CAPACITIES };
    }
    return { sizeTitle: "Beden", sizeList: CLOTHING_SIZES };
  }, [isShoe, isPants, isSuit, isTech, isCosmetics, isHome]);

  // Dinamik Marka Listesi
  const availableBrands = useMemo(() => {
    if (isShoe) return BRAND_POOLS.shoes;
    if (isTech) return BRAND_POOLS.tech;
    if (isCosmetics) return BRAND_POOLS.cosmetics;
    if (isHome) return BRAND_POOLS.home;
    return BRAND_POOLS.clothing;
  }, [isShoe, isTech, isCosmetics, isHome]);

  // Dinamik Kalıp Listesi
  const fitList = useMemo(() => {
    if (isPants) return PANTS_FITS;
    return CLOTHING_FITS;
  }, [isPants]);

  const toggleSection = (key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleArrayToggle = (field: keyof FilterState, value: string) => {
    const currentList = (filters[field] as string[]) || [];
    const exists = currentList.includes(value);
    const updated = exists ? currentList.filter(item => item !== value) : [...currentList, value];
    onChange({ ...filters, [field]: updated });
  };

  const filteredBrands = useMemo(() => {
    if (!brandSearch) return availableBrands;
    return availableBrands.filter(b => b.toLowerCase().includes(brandSearch.toLowerCase()));
  }, [availableBrands, brandSearch]);

  const hasActiveFilters = 
    filters.category !== "all" ||
    filters.gender.length > 0 ||
    filters.sizes.length > 0 ||
    filters.brands.length > 0 ||
    filters.collarTypes.length > 0 ||
    filters.fits.length > 0 ||
    Boolean(filters.minPrice || filters.maxPrice || filters.minDiscount) ||
    filters.stores.length > 0;

  return (
    <aside className="w-full bg-white rounded-3xl border border-gray-200 shadow-sm divide-y divide-gray-100 font-sans text-gray-800 sticky top-24">
      {/* Üst Başlık & Temizle */}
      <div className="p-5 flex items-center justify-between">
        <div>
          <h2 className="text-base font-black text-gray-900 tracking-tight flex items-center gap-2">
            <i className="ri-sound-module-line text-rose-600"></i> FİLTRELER
          </h2>
          <p className="text-[11px] text-gray-400 font-semibold mt-0.5">
            {totalResultsCount} İndirimli Fırsat
          </p>
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onReset}
            className="text-xs font-bold text-rose-600 hover:text-rose-700 bg-rose-50 px-2.5 py-1 rounded-lg transition-colors cursor-pointer"
          >
            Temizle
          </button>
        )}
      </div>

      {/* 1. KATEGORİ SEÇİMİ */}
      <div className="p-4">
        <button
          type="button"
          onClick={() => toggleSection("category")}
          className="w-full flex items-center justify-between text-xs font-black uppercase text-gray-900 tracking-wider mb-2 cursor-pointer"
        >
          <span>Kategori</span>
          <i className={`ri-arrow-down-s-line text-base text-gray-400 transition-transform ${openSections.category ? "rotate-180" : ""}`}></i>
        </button>
        {openSections.category && (
          <div className="space-y-1 mt-2 max-h-56 overflow-y-auto pr-1">
            {CATEGORIES.map(cItem => {
              const isSelected = filters.category === cItem.id || (cItem.id !== "all" && filters.category.includes(cItem.id));
              return (
                <button
                  key={cItem.id}
                  type="button"
                  onClick={() => onChange({ ...filters, category: cItem.id })}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-bold text-left transition-all cursor-pointer ${
                    isSelected
                      ? "bg-rose-600 text-white shadow-sm font-black"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <i className={`${cItem.icon} text-sm`}></i>
                  <span>{cItem.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 2. CİNSİYET SEÇİMİ (Sadece Moda, Ayakkabı ve Parfümde Göster) */}
      {(!isTech && !isHome) && (
        <div className="p-4">
          <button
            type="button"
            onClick={() => toggleSection("gender")}
            className="w-full flex items-center justify-between text-xs font-black uppercase text-gray-900 tracking-wider mb-2 cursor-pointer"
          >
            <span>Cinsiyet</span>
            <i className={`ri-arrow-down-s-line text-base text-gray-400 transition-transform ${openSections.gender ? "rotate-180" : ""}`}></i>
          </button>
          {openSections.gender && (
            <div className="space-y-2 mt-2">
              {GENDERS.map(g => (
                <label key={g} className="flex items-center gap-2 text-xs font-bold text-gray-700 cursor-pointer hover:text-rose-600 transition-colors">
                  <input
                    type="checkbox"
                    checked={filters.gender.includes(g)}
                    onChange={() => handleArrayToggle("gender", g)}
                    className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500 border-gray-300 cursor-pointer"
                  />
                  <span>{g}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 3. DİNAMİK BEDEN / NUMARA / KAPASİTE SEÇİMİ */}
      <div className="p-4">
        <button
          type="button"
          onClick={() => toggleSection("size")}
          className="w-full flex items-center justify-between text-xs font-black uppercase text-gray-900 tracking-wider mb-2 cursor-pointer"
        >
          <span>{sizeTitle}</span>
          <i className={`ri-arrow-down-s-line text-base text-gray-400 transition-transform ${openSections.size ? "rotate-180" : ""}`}></i>
        </button>
        {openSections.size && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {sizeList.map(sz => {
              const isSelected = filters.sizes.includes(sz);
              return (
                <button
                  key={sz}
                  type="button"
                  onClick={() => handleArrayToggle("sizes", sz)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all border cursor-pointer ${
                    isSelected
                      ? "bg-gray-900 text-white border-gray-900 shadow-xs scale-105"
                      : "bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-400 hover:bg-white"
                  }`}
                >
                  {sz}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 4. MARKA SEÇİMİ */}
      <div className="p-4">
        <button
          type="button"
          onClick={() => toggleSection("brand")}
          className="w-full flex items-center justify-between text-xs font-black uppercase text-gray-900 tracking-wider mb-2 cursor-pointer"
        >
          <span>Marka</span>
          <i className={`ri-arrow-down-s-line text-base text-gray-400 transition-transform ${openSections.brand ? "rotate-180" : ""}`}></i>
        </button>
        {openSections.brand && (
          <div className="space-y-2 mt-2">
            <div className="relative mb-2">
              <input
                type="text"
                placeholder="Marka ara..."
                value={brandSearch}
                onChange={e => setBrandSearch(e.target.value)}
                className="w-full text-xs font-medium pl-7 pr-2 py-1.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-rose-500"
              />
              <i className="ri-search-line absolute left-2.5 top-2 text-gray-400 text-xs"></i>
            </div>
            <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1">
              {filteredBrands.map(b => (
                <label key={b} className="flex items-center gap-2 text-xs font-bold text-gray-700 cursor-pointer hover:text-rose-600 transition-colors">
                  <input
                    type="checkbox"
                    checked={filters.brands.includes(b)}
                    onChange={() => handleArrayToggle("brands", b)}
                    className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500 border-gray-300 cursor-pointer"
                  />
                  <span>{b}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 5. YAKA TİPİ (Sadece Üst Giyimde Göster) */}
      {isUpperClothing && (
        <div className="p-4">
          <button
            type="button"
            onClick={() => toggleSection("collar")}
            className="w-full flex items-center justify-between text-xs font-black uppercase text-gray-900 tracking-wider mb-2 cursor-pointer"
          >
            <span>Yaka Tipi</span>
            <i className={`ri-arrow-down-s-line text-base text-gray-400 transition-transform ${openSections.collar ? "rotate-180" : ""}`}></i>
          </button>
          {openSections.collar && (
            <div className="space-y-2 mt-2">
              {COLLAR_TYPES.map(c => (
                <label key={c} className="flex items-center gap-2 text-xs font-bold text-gray-700 cursor-pointer hover:text-rose-600 transition-colors">
                  <input
                    type="checkbox"
                    checked={filters.collarTypes.includes(c)}
                    onChange={() => handleArrayToggle("collarTypes", c)}
                    className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500 border-gray-300 cursor-pointer"
                  />
                  <span>{c}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 6. KALIP / FİT (Sadece Üst Giyim ve Pantolonda Göster) */}
      {(isUpperClothing || isPants || isSuit) && (
        <div className="p-4">
          <button
            type="button"
            onClick={() => toggleSection("fit")}
            className="w-full flex items-center justify-between text-xs font-black uppercase text-gray-900 tracking-wider mb-2 cursor-pointer"
          >
            <span>Kalıp / Fit</span>
            <i className={`ri-arrow-down-s-line text-base text-gray-400 transition-transform ${openSections.fit ? "rotate-180" : ""}`}></i>
          </button>
          {openSections.fit && (
            <div className="space-y-2 mt-2">
              {fitList.map(f => (
                <label key={f} className="flex items-center gap-2 text-xs font-bold text-gray-700 cursor-pointer hover:text-rose-600 transition-colors">
                  <input
                    type="checkbox"
                    checked={filters.fits.includes(f)}
                    onChange={() => handleArrayToggle("fits", f)}
                    className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500 border-gray-300 cursor-pointer"
                  />
                  <span>{f}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 7. FİYAT ARALIĞI (TL) */}
      <div className="p-4">
        <button
          type="button"
          onClick={() => toggleSection("price")}
          className="w-full flex items-center justify-between text-xs font-black uppercase text-gray-900 tracking-wider mb-2 cursor-pointer"
        >
          <span>Fiyat Aralığı (TL)</span>
          <i className={`ri-arrow-down-s-line text-base text-gray-400 transition-transform ${openSections.price ? "rotate-180" : ""}`}></i>
        </button>
        {openSections.price && (
          <div className="mt-2 space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="En Az"
                value={filters.minPrice}
                onChange={e => onChange({ ...filters, minPrice: e.target.value })}
                className="w-full text-xs font-bold px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-rose-500"
              />
              <span className="text-gray-400 font-bold">-</span>
              <input
                type="number"
                placeholder="En Çok"
                value={filters.maxPrice}
                onChange={e => onChange({ ...filters, maxPrice: e.target.value })}
                className="w-full text-xs font-bold px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-rose-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* 8. İNDİRİM ORANI */}
      <div className="p-4">
        <button
          type="button"
          onClick={() => toggleSection("discount")}
          className="w-full flex items-center justify-between text-xs font-black uppercase text-gray-900 tracking-wider mb-2 cursor-pointer"
        >
          <span>İndirim Oranı</span>
          <i className={`ri-arrow-down-s-line text-base text-gray-400 transition-transform ${openSections.discount ? "rotate-180" : ""}`}></i>
        </button>
        {openSections.discount && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {[20, 30, 40, 50].map(rate => {
              const isSelected = filters.minDiscount === rate;
              return (
                <button
                  key={rate}
                  type="button"
                  onClick={() => onChange({ ...filters, minDiscount: isSelected ? null : rate })}
                  className={`px-2.5 py-1 rounded-xl text-xs font-black transition-all border cursor-pointer ${
                    isSelected
                      ? "bg-rose-600 text-white border-rose-600 shadow-xs"
                      : "bg-rose-50/50 text-rose-700 border-rose-100 hover:bg-rose-100"
                  }`}
                >
                  %{rate}+ İndirim
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 9. MAĞAZALAR */}
      <div className="p-4">
        <button
          type="button"
          onClick={() => toggleSection("store")}
          className="w-full flex items-center justify-between text-xs font-black uppercase text-gray-900 tracking-wider mb-2 cursor-pointer"
        >
          <span>Mağazalar</span>
          <i className={`ri-arrow-down-s-line text-base text-gray-400 transition-transform ${openSections.store ? "rotate-180" : ""}`}></i>
        </button>
        {openSections.store && (
          <div className="space-y-2 mt-2 max-h-36 overflow-y-auto pr-1">
            {STORES.map(st => (
              <label key={st} className="flex items-center gap-2 text-xs font-bold text-gray-700 cursor-pointer hover:text-rose-600 transition-colors">
                <input
                  type="checkbox"
                  checked={filters.stores.includes(st)}
                  onChange={() => handleArrayToggle("stores", st)}
                  className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500 border-gray-300 cursor-pointer"
                />
                <span>{st}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
